/**
 * Character budgets.
 *
 * A translation is not correct merely because it means the right thing.
 * French and Portuguese run about 25% longer than English and German
 * about 35%, and short strings expand furthest of all - "OK" becomes
 * "Einverstanden". A label that fits in English and clips in German is
 * a bug that only ever shows up on somebody else's phone, so it is
 * caught here instead, by arithmetic.
 *
 * The rule the translations themselves are written to is in
 * docs/i18n-plan.md section 6: the shortest phrasing a native speaker
 * would actually use. This module is the enforcement, not the goal -
 * a translation that fits by being unnatural has failed differently.
 */
import overrides from "./catalogue/_budget.json";

/**
 * The standard expansion allowance, by source length.
 *
 * Industry table (IBM/Microsoft localisation guidance). Short strings
 * get proportionally more room because a two-character string has
 * nowhere to go otherwise.
 */
const TABLE: readonly [number, number][] = [
  [10, 2.0],
  [20, 1.8],
  [30, 1.6],
  [50, 1.4],
  [Infinity, 1.3],
];

export function defaultBudget(english: string): number {
  const n = english.length;
  const factor = TABLE.find(([limit]) => n <= limit)![1];
  // +2 absorbs a trailing accent or an article on a very short label
  // without opening the door on a long one.
  return Math.ceil(n * factor) + 2;
}

/**
 * `_budget.json` carries `$`-prefixed prose alongside the numbers - the
 * reason each group of limits exists, next to the limits themselves,
 * because a bare number in a config file is a number nobody dares
 * change. Only the numeric entries are budgets.
 */
const EXPLICIT: Record<string, number> = Object.fromEntries(
  Object.entries(overrides as Record<string, unknown>).filter(
    (entry): entry is [string, number] => typeof entry[1] === "number",
  ),
);

/**
 * The hard limit for one key.
 *
 * An explicit entry in `_budget.json` always wins, INCLUDING when it is
 * tighter than the source English - that is the point. A tab label with
 * room for nine characters has room for nine characters whatever the
 * English happens to be.
 */
export function budgetFor(key: string, english: string): number {
  const explicit = EXPLICIT[key];
  if (typeof explicit === "number") return explicit;
  return defaultBudget(english);
}

export function hasExplicitBudget(key: string): boolean {
  return typeof EXPLICIT[key] === "number";
}

/** Placeholders i18next will interpolate. Losing one breaks the string. */
export function placeholders(value: string): string[] {
  return [...value.matchAll(/\{\{\s*([A-Za-z0-9_]+)[^}]*\}\}/g)]
    .map((m) => m[1])
    .sort();
}
